/**
 * EquipeV2 — Gestion de l'équipe (membres, rôles, périmètre clubs).
 *
 * Réservée aux admins (côté UI ET côté serveur : l'écriture de ep:users est
 * gatée admin dans server/lib/access.js). Permet de créer un membre, d'éditer
 * son rôle canonique (admin | comm | directeur | ja) et ses clubs, et de
 * l'activer / désactiver (aucune suppression définitive).
 *
 * Source : ep:users (lecture live via useUsers). Les mots de passe ne sont PAS
 * exposés ici (expurgés côté serveur pour les non-admins ; jamais édités ici).
 *
 * Lot 1 du chantier « équipe & SSO » — cf. docs/cadrage-equipe-sso.md.
 * Le rapprochement SSO robuste (ssoId) et le retrait des mots de passe en clair
 * relèvent des lots 2 et 3.
 */
import React, { useMemo, useState } from "react";
import { useUsers, useClubs } from "../hooks/useV1Data";
import { createUser, updateUser, setUserActive } from "../hooks/useV1Write";
import { isAdmin as isAdminUser, roleOf, ROLE_LABELS, ASSIGNABLE_ROLES } from "../lib/roles";
import { IconUsers, IconPlus, IconX } from "../icons";

const ACCENT = "#0071CD";

const ROLE_COLOR = {
  admin:     "#7C3AED",
  comm:      "#0071CD",
  directeur: "#0E9F6E",
  ja:        "#D97706",
  guest:     "#64748B",
};

function nameOf(u) {
  if (!u) return "";
  return (
    [u.firstName, u.lastName].filter(Boolean).join(" ").trim() ||
    u.name || u.displayName || u.email || "Sans nom"
  );
}

function initials(name) {
  if (!name) return "?";
  const parts = String(name).split(" ").filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return (parts[0][0] || "?").toUpperCase();
}

/* ─────────────────────────── Modal création / édition ─────────────────────── */
function MemberModal({ member, clubs, onClose, onSaved }) {
  const editing = !!member;
  const [firstName, setFirstName] = useState(member?.firstName || "");
  const [lastName, setLastName]   = useState(member?.lastName || "");
  const [email, setEmail]         = useState(member?.email || "");
  const [roleComm, setRoleComm]   = useState(editing ? roleOf(member) : "comm");
  const [memberClubs, setMemberClubs] = useState(
    Array.isArray(member?.clubs) ? member.clubs.map(String) : []
  );
  const [active, setActive] = useState(member ? member.active !== false : true);
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState(null);

  const isAdminRole = roleComm === "admin";

  function toggleClub(id) {
    const s = String(id);
    setMemberClubs((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));
  }

  async function handleSubmit() {
    const em = String(email || "").toLowerCase().trim();
    if (!firstName.trim() && !lastName.trim()) { setError("Renseigne au moins un prénom ou un nom."); return; }
    if (!em || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(em)) { setError("Email invalide — il sert au rapprochement SSO."); return; }
    setSaving(true);
    setError(null);
    // Un admin voit tous les clubs : on ne stocke pas de périmètre pour lui.
    const payload = {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      email: em,
      roleComm,
      clubs: isAdminRole ? [] : memberClubs,
      active,
    };
    try {
      if (editing) await updateUser(member.id, payload);
      else await createUser(payload);
      onSaved();
    } catch (e) {
      const msg = String(e && e.message || "");
      setError(msg.includes("403")
        ? "Écriture refusée : action réservée aux administrateurs."
        : "L'enregistrement a échoué. Vérifie ta connexion et réessaie.");
      setSaving(false);
    }
  }

  const overlay = {
    position: "fixed", inset: 0, background: "rgba(26,26,28,.5)",
    display: "flex", alignItems: "center", justifyContent: "center", padding: 20, zIndex: 1000,
  };
  const card = {
    width: "min(520px,100%)", maxHeight: "90vh", overflowY: "auto",
    background: "#fff", borderRadius: 14, padding: "20px 22px",
    boxShadow: "0 16px 48px rgba(0,0,0,.24)", fontFamily: "inherit",
  };
  const label = { fontSize: 13, fontWeight: 600, color: "var(--text, #1a1a1c)", margin: "0 0 8px" };
  const field = { marginBottom: 16 };
  const inputBase = {
    width: "100%", padding: "9px 12px", borderRadius: 9, border: "1.5px solid #E2E8F0",
    fontSize: 13, fontFamily: "inherit", boxSizing: "border-box", background: "#F8FAFC", outline: "none",
  };

  return (
    <div style={overlay} onClick={onClose}>
      <div style={card} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
          <h2 style={{ fontSize: 17, fontWeight: 800, margin: 0, color: "var(--text, #1a1a1c)" }}>
            {editing ? "Modifier le membre" : "Ajouter un membre"}
          </h2>
          <button onClick={onClose} aria-label="Fermer" style={{ background: "none", border: "none", cursor: "pointer", color: "#94A3B8", lineHeight: 1, padding: 2 }}>
            <IconX size={20} />
          </button>
        </div>

        {/* Prénom + Nom */}
        <div style={{ display: "flex", gap: 14 }}>
          <div style={{ ...field, flex: 1 }}>
            <p style={label}>Prénom</p>
            <input value={firstName} onChange={(e) => setFirstName(e.target.value)} style={inputBase} placeholder="Mélissa" />
          </div>
          <div style={{ ...field, flex: 1 }}>
            <p style={label}>Nom</p>
            <input value={lastName} onChange={(e) => setLastName(e.target.value)} style={inputBase} placeholder="Dupont" />
          </div>
        </div>

        {/* Email */}
        <div style={field}>
          <p style={label}>Email <span style={{ fontWeight: 400, color: "#94A3B8", fontSize: 12 }}>· clé de rapprochement SSO</span></p>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} style={inputBase} placeholder="prenom@espritpadel.com" autoComplete="off" />
        </div>

        {/* Rôle */}
        <div style={field}>
          <p style={label}>Rôle</p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {ASSIGNABLE_ROLES.map((r) => {
              const sel = roleComm === r;
              return (
                <button
                  key={r}
                  onClick={() => setRoleComm(r)}
                  style={{
                    padding: "7px 14px", borderRadius: 999, cursor: "pointer", fontSize: 13,
                    fontFamily: "inherit", fontWeight: sel ? 700 : 500,
                    border: sel ? `1.5px solid ${ROLE_COLOR[r]}` : "1px solid #E2E8F0",
                    background: sel ? "#fff" : "#F8FAFC",
                    color: sel ? ROLE_COLOR[r] : "#64748B",
                  }}
                >{ROLE_LABELS[r]}</button>
              );
            })}
          </div>
        </div>

        {/* Clubs */}
        <div style={field}>
          <p style={label}>Clubs <span style={{ fontWeight: 400, color: "#94A3B8", fontSize: 12 }}>· périmètre</span></p>
          {isAdminRole ? (
            <div style={{ fontSize: 13, color: "#64748B", background: "#F1F5F9", borderRadius: 9, padding: "10px 12px" }}>
              Un responsable comm (admin) a accès à <strong>tous les clubs</strong>.
            </div>
          ) : (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {(clubs || []).map((c) => {
                const id = String(c.id);
                const sel = memberClubs.includes(id);
                return (
                  <button
                    key={id}
                    onClick={() => toggleClub(id)}
                    style={{
                      padding: "6px 12px", borderRadius: 999, cursor: "pointer", fontSize: 12.5,
                      fontFamily: "inherit", fontWeight: sel ? 700 : 500,
                      border: sel ? `1.5px solid ${ACCENT}` : "1px solid #E2E8F0",
                      background: sel ? "#EFF6FF" : "#fff",
                      color: sel ? ACCENT : "var(--text, #1a1a1c)",
                    }}
                  >{c.name || c.nom || id}</button>
                );
              })}
              {(!clubs || clubs.length === 0) && (
                <span style={{ fontSize: 13, color: "#94A3B8" }}>Aucun club disponible.</span>
              )}
            </div>
          )}
        </div>

        {/* Actif */}
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--text, #1a1a1c)", marginBottom: 6, cursor: "pointer" }}>
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
          Membre actif
        </label>

        {error && <p style={{ fontSize: 13, color: "#B23B3B", margin: "4px 0 0" }}>{error}</p>}

        {/* Actions */}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 18 }}>
          <button onClick={onClose} disabled={saving}
            style={{ padding: "9px 16px", borderRadius: 9, border: "1px solid #CBD5E1", background: "transparent", fontSize: 14, cursor: "pointer", fontFamily: "inherit", color: "var(--text, #1a1a1c)" }}>
            Annuler
          </button>
          <button onClick={handleSubmit} disabled={saving}
            style={{ padding: "9px 20px", borderRadius: 9, border: "none", background: saving ? "#94A3B8" : ACCENT, color: "#fff", fontSize: 14, fontWeight: 700, cursor: saving ? "default" : "pointer", fontFamily: "inherit" }}>
            {saving ? "Enregistrement…" : editing ? "Enregistrer" : "Ajouter"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────── Ligne membre ──────────────────────────────────── */
function MemberRow({ u, clubsById, onEdit, onToggleActive }) {
  const name = nameOf(u);
  const role = roleOf(u);
  const inactive = u.active === false;
  const clubNames = (Array.isArray(u.clubs) ? u.clubs : [])
    .map((id) => clubsById[String(id)])
    .filter(Boolean);

  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 14, padding: "12px 16px",
      borderBottom: "1px solid #EEF2F7", opacity: inactive ? 0.55 : 1,
    }}>
      <span style={{
        width: 36, height: 36, borderRadius: "50%", flexShrink: 0,
        background: "#EFF6FF", color: ACCENT, display: "flex", alignItems: "center",
        justifyContent: "center", fontSize: 13, fontWeight: 700,
      }}>{initials(name)}</span>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text, #1a1a1c)" }}>
          {name}
          {inactive && <span style={{ fontSize: 11.5, color: "#94A3B8", fontWeight: 500 }}> · inactif</span>}
        </div>
        <div style={{ fontSize: 12.5, color: "#64748B", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {u.email || "—"}
        </div>
      </div>

      {/* Rôle */}
      <span style={{
        fontSize: 12, fontWeight: 700, padding: "4px 10px", borderRadius: 999, flexShrink: 0,
        color: ROLE_COLOR[role], background: `${ROLE_COLOR[role]}14`,
      }}>{ROLE_LABELS[role]}</span>

      {/* Clubs */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 4, justifyContent: "flex-end", maxWidth: 220 }}>
        {role === "admin" ? (
          <span style={{ fontSize: 11.5, color: "#94A3B8" }}>Tous les clubs</span>
        ) : clubNames.length ? (
          clubNames.map((n, i) => (
            <span key={i} style={{ fontSize: 11.5, color: "#475569", background: "#F1F5F9", borderRadius: 6, padding: "2px 7px" }}>{n}</span>
          ))
        ) : (
          <span style={{ fontSize: 11.5, color: "#CBD5E1" }}>Aucun club</span>
        )}
      </div>

      {/* Actions */}
      <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
        <button onClick={() => onEdit(u)}
          style={{ padding: "6px 12px", borderRadius: 8, border: "1px solid #E2E8F0", background: "#fff", fontSize: 12.5, cursor: "pointer", fontFamily: "inherit", color: "var(--text, #1a1a1c)" }}>
          Modifier
        </button>
        <button onClick={() => onToggleActive(u)}
          style={{ padding: "6px 12px", borderRadius: 8, border: "1px solid #E2E8F0", background: "#fff", fontSize: 12.5, cursor: "pointer", fontFamily: "inherit", color: inactive ? ACCENT : "#B23B3B" }}>
          {inactive ? "Réactiver" : "Désactiver"}
        </button>
      </div>
    </div>
  );
}

/* ─────────────────────────── Page ──────────────────────────────────────────── */
export default function EquipeV2({ currentUser }) {
  const { users } = useUsers();
  const { clubs } = useClubs();
  const [modalMember, setModalMember] = useState(null); // objet membre | {} (nouveau) | null (fermé)
  const [showInactive, setShowInactive] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const isAdmin = isAdminUser(currentUser);

  const clubsById = useMemo(() => {
    const map = {};
    (clubs || []).forEach((c) => { if (c && c.id != null) map[String(c.id)] = c.name || c.nom || String(c.id); });
    return map;
  }, [clubs]);

  const list = useMemo(() => {
    const arr = (users || []).filter(Boolean);
    const visible = showInactive ? arr : arr.filter((u) => u.active !== false);
    return visible.slice().sort((a, b) => nameOf(a).localeCompare(nameOf(b)));
  }, [users, showInactive]);

  async function handleToggleActive(u) {
    setBusyId(u.id);
    try { await setUserActive(u.id, u.active === false); } catch { /* onSnapshot ne rafraîchira pas */ }
    setBusyId(null);
  }

  if (!isAdmin) {
    return (
      <div style={{ padding: "48px 28px", display: "flex", justifyContent: "center" }}>
        <div style={{ textAlign: "center", maxWidth: 360 }}>
          <div style={{ color: "#CBD5E1", marginBottom: 12, display: "flex", justifyContent: "center" }}>
            <IconUsers size={40} />
          </div>
          <div style={{ fontSize: 16, fontWeight: 700, color: "var(--text, #1a1a1c)", marginBottom: 6 }}>Accès réservé</div>
          <div style={{ fontSize: 13.5, color: "#64748B", lineHeight: 1.5 }}>
            La gestion de l'équipe est réservée aux administrateurs. Contacte le responsable
            communication pour toute modification de rôle ou de club.
          </div>
        </div>
      </div>
    );
  }

  const inactiveCount = (users || []).filter((u) => u && u.active === false).length;

  return (
    <div style={{ padding: "24px 28px", maxWidth: 900, margin: "0 auto" }}>
      {/* En-tête */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, marginBottom: 18, flexWrap: "wrap" }}>
        <div>
          <p style={{ fontSize: 13.5, color: "#64748B", margin: 0 }}>
            {list.length} membre{list.length > 1 ? "s" : ""}{showInactive ? "" : " actif" + (list.length > 1 ? "s" : "")}
          </p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {inactiveCount > 0 && (
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, color: "#64748B", cursor: "pointer" }}>
              <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
              Afficher les inactifs ({inactiveCount})
            </label>
          )}
          <button onClick={() => setModalMember({})}
            style={{ display: "flex", alignItems: "center", gap: 7, padding: "9px 16px", borderRadius: 9, border: "none", background: ACCENT, color: "#fff", fontSize: 13.5, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
            <IconPlus size={15} /> Ajouter un membre
          </button>
        </div>
      </div>

      {/* Liste */}
      <div style={{ background: "#fff", border: "1px solid #EEF2F7", borderRadius: 12, overflow: "hidden" }}>
        {list.length === 0 ? (
          <div style={{ padding: "40px 20px", textAlign: "center", fontSize: 13.5, color: "#94A3B8" }}>
            Aucun membre à afficher.
          </div>
        ) : (
          list.map((u) => (
            <div key={String(u.id)} style={{ pointerEvents: busyId === u.id ? "none" : "auto" }}>
              <MemberRow
                u={u}
                clubsById={clubsById}
                onEdit={(m) => setModalMember(m)}
                onToggleActive={handleToggleActive}
              />
            </div>
          ))
        )}
      </div>

      {modalMember !== null && (
        <MemberModal
          member={modalMember && modalMember.id != null ? modalMember : null}
          clubs={clubs}
          onClose={() => setModalMember(null)}
          onSaved={() => setModalMember(null)}
        />
      )}
    </div>
  );
}
